import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, { setAuthToken } from '@/lib/api';

const AuthContext = createContext(null);

const ROLE_PERMISSIONS = {
  admin: ['*'],
  manager: ['business.write', 'business.delete'],
  staff: ['business.write'],
  viewer: [],
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('fpccs_token');
    if (token) {
      setAuthToken(token);
      api.get('/auth/me')
        .then(({ data }) => {
          setUser(data.user);
          localStorage.setItem('fpccs_user', JSON.stringify(data.user));
        })
        .catch(() => {
          localStorage.removeItem('fpccs_token');
          localStorage.removeItem('fpccs_user');
          setAuthToken(null);
          setUser(null);
        })
        .finally(() => setLoading(false));
      return;
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token, user } = res.data;
    localStorage.setItem('fpccs_token', token);
    localStorage.setItem('fpccs_user', JSON.stringify(user));
    setAuthToken(token);
    setUser(user);
    return user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('fpccs_token');
    localStorage.removeItem('fpccs_user');
    setAuthToken(null);
    setUser(null);
  }, []);

  const can = useCallback((permission) => {
    const permissions = ROLE_PERMISSIONS[user?.role] || [];
    return permissions.includes('*') || permissions.includes(permission);
  }, [user?.role]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
